import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { groupsApi } from '@app/api/organization';
import { useAuth } from '@app/auth/AuthContext';
import type { Group, GroupCreateRequest } from '@app/types';
import { logger } from '@app/utils/logger';

interface GroupContextType {
  groups: Group[];
  selectedGroupId: string | null;
  selectedGroup: Group | null;
  setSelectedGroupId: (groupId: string | null) => void;
  isLoading: boolean;
  refetchGroups: () => Promise<void>;
  updateGroupInviteCode: (groupId: string, inviteCode: string | null, inviteCodeExpiry: string | null) => void;
  didAutoCreateDefault: boolean;
}

const GroupContext = createContext<GroupContextType | undefined>(undefined);

export const GroupProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupIdState] = useState<string | null>(() => {
    return localStorage.getItem('selectedGroupId') || null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [didAutoCreateDefault, setDidAutoCreateDefault] = useState(false);
  const autoCreateAttempted = useRef(false);

  const fetchGroups = useCallback(async () => {
    if (!user?.churchId) return;
    setIsLoading(true);
    try {
      const data = await groupsApi.getByChurch(user.churchId);
      const activeGroups = data.filter(g => g.isActive);
      setGroups(activeGroups);

      // Auto-create a default "Worship Team" for churches with no groups
      const isAdmin = user.userRole === 'Admin';
      if (activeGroups.length === 0 && isAdmin && !autoCreateAttempted.current) {
        autoCreateAttempted.current = true;
        try {
          const request: GroupCreateRequest = {
            churchId: user.churchId,
            name: 'Worship Team',
            description: 'Your primary worship team',
            leaderIds: [user.id],
            createdBy: user.id,
          };
          const newGroup = await groupsApi.create(user.churchId, request);
          setGroups([newGroup]);
          setSelectedGroupIdState(newGroup.id);
          localStorage.setItem('selectedGroupId', newGroup.id);
          setDidAutoCreateDefault(true);
          logger.info('Auto-created default Worship Team for church', user.churchId);
        } catch (err) {
          logger.error('Failed to auto-create default group:', err);
        }
      }
    } catch (err) {
      console.error('Failed to fetch groups:', err);
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.churchId, user?.id, user?.userRole]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Persist selection to localStorage
  const setSelectedGroupId = useCallback((groupId: string | null) => {
    setSelectedGroupIdState(groupId);
    if (groupId) {
      localStorage.setItem('selectedGroupId', groupId);
    } else {
      localStorage.removeItem('selectedGroupId');
    }
  }, []);

  // Clear selection if the stored groupId isn't in the loaded groups
  useEffect(() => {
    if (groups.length > 0 && selectedGroupId && !groups.find(g => g.id === selectedGroupId)) {
      setSelectedGroupId(null);
    }
  }, [groups, selectedGroupId, setSelectedGroupId]);

  const updateGroupInviteCode = useCallback((groupId: string, inviteCode: string | null, inviteCodeExpiry: string | null) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, inviteCode: inviteCode ?? undefined, inviteCodeExpiry: inviteCodeExpiry ?? undefined } : g));
  }, []);

  const selectedGroup = groups.find(g => g.id === selectedGroupId) || null;

  return (
    <GroupContext.Provider
      value={{
        groups,
        selectedGroupId,
        selectedGroup,
        setSelectedGroupId,
        isLoading,
        refetchGroups: fetchGroups,
        updateGroupInviteCode,
        didAutoCreateDefault,
      }}
    >
      {children}
    </GroupContext.Provider>
  );
};

export const useGroup = (): GroupContextType => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error('useGroup must be used within a GroupProvider');
  }
  return context;
};
